import { Loader2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { TableRow, TableCell } from './ui/table';
import AppShell from './AppShell';

/**
 * Reusable admin page wrapper with loading state
 */
export function AdminPageShell({ children, loading, title, subtitle, actions, testId, maxWidth = '6xl' }) {
  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className={`p-6 md:p-8 max-w-${maxWidth} animate-fade-in`} data-testid={testId}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>{title}</h1>
            {subtitle && <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>}
          </div>
          {actions && <div className="flex gap-2">{actions}</div>}
        </div>
        {children}
      </div>
    </AppShell>
  );
}

/**
 * Stat card for dashboard-style metrics
 */
export function StatCard({ label, value, icon: Icon, color, index = 0 }) {
  return (
    <div className={`rounded-lg border bg-card p-5 stagger-${index + 1} animate-fade-in`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className={`text-3xl font-bold mt-2 ${color || ''}`} style={{ fontFamily: 'DM Sans' }}>{value}</p>
    </div>
  );
}

/**
 * Empty table row placeholder
 */
export function EmptyRow({ colSpan, message = 'No data found' }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">{message}</TableCell>
    </TableRow>
  );
}

/**
 * Sortable table header icon
 */
export function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp className="w-3 h-3 ml-1 inline" />
    : <ArrowDown className="w-3 h-3 ml-1 inline" />;
}

/**
 * Hook-like helper for sort state management.
 * Usage: const { sortCol, sortDir, toggleSort, sortData } = useSortState('name');
 */
export function createSortHelpers(sortCol, sortDir, setSortCol, setSortDir) {
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortData = (data, getValueFn) => {
    return [...data].sort((a, b) => {
      const va = getValueFn(a, sortCol);
      const vb = getValueFn(b, sortCol);
      const cmp = typeof va === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  return { toggleSort, sortData };
}
